import { Component, Input } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { CodeEditorComponent, createProviders } from '../code-editor.component';
import { ITypeConfig } from '../../designer-config-service/common/parameter-designer.config';
import { Type } from '../../type';
import { isEmpty, isNil, cloneDeep } from 'lodash-es';

interface IContributionModel {
  state: number;
}

interface IEditorContribution extends monaco.editor.IEditorContribution {
  model: IContributionModel;
}

@Component({
  selector: 'app-expr-input',
  templateUrl: './expr-input.component.html',
  styleUrls: ['./expr-input.component.scss'],
  providers: createProviders(ExprInputComponent),
  standalone: false
})

export class ExprInputComponent extends CodeEditorComponent {
  @Input() type: ITypeConfig;
  @Input() ignoreSecure: boolean;
  @Input() readonly: boolean = false;

  createEditorOptions(): monaco.editor.IStandaloneEditorConstructionOptions {
    /**
     * Options for single line mode, see https://github.com/microsoft/monaco-editor/issues/2009
     */
    return {
      model: monaco.editor.createModel('', 'fakeTypescript', monaco.Uri.parse(`file:///main_${this.id}.ts`)),
      language: 'fakeTypescript',
      lineNumbers: 'off',
      lineNumbersMinChars: 0,
      overviewRulerLanes: 0,
      overviewRulerBorder: false,
      lineDecorationsWidth: 0,
      hideCursorInOverviewRuler: true,
      glyphMargin: false,
      folding: false,
      scrollBeyondLastColumn: 0,
      scrollBeyondLastLine: false,
      scrollbar: { horizontal: 'hidden', vertical: 'hidden', alwaysConsumeMouseWheel: false },
      find: { addExtraSpaceOnTop: false, autoFindInSelection: 'never', seedSearchStringFromSelection: 'never' },
      minimap: { enabled: false },
      lineHeight: 24,
      extraEditorClassName: 'datex-monaco-editor',
      theme: 'datex-inline-expr',
      cursorStyle: 'line-thin',
      renderLineHighlight: 'none',
      fixedOverflowWidgets: true,
      automaticLayout: true,
      contextmenu: false,
      readOnly: this.readonly
    };
  }

  // TAG: TypeConfigsAsFrontend
  getTypeConfigsAsFrontend(exprTypes: ITypeConfig[]): ITypeConfig[] {
    const result: ITypeConfig[] = [];
    for (const exprType of exprTypes) {
      result.push(this.getTypeConfigAsFrontend(exprType));
    }
    return result;
  }

  cloneTypeConfigWithBaseProps(exprType: ITypeConfig): ITypeConfig {
    return {
      id: exprType.id,
      type: exprType.type,
      isCollection: exprType.isCollection,
      required: exprType.required,
      description: exprType.description,
      isSecured: exprType.isSecured,
      isConstant: exprType.isConstant,
      constantValue: exprType.constantValue,
      oneOf: cloneDeep(exprType.oneOf)
    };
  }

  getTypeConfigAsFrontend(exprType: ITypeConfig): ITypeConfig {
    if (!exprType) {
      return exprType;
    }
    // TAG: TypeConfig parsing
    if (exprType.type === Type.object) {
      if (isEmpty(exprType.objectType) === false) {
        // customType object: throw
        throw new Error('No support for custom objects into expression code');
      }
      else {
        if (isNil(exprType.objectTypeDef)) {
          // any : just clone
          const newexprType = this.cloneTypeConfigWithBaseProps(exprType);
          return newexprType;
        }
        else {
          // inline object: process objectTypeDef
          const newexprType = this.cloneTypeConfigWithBaseProps(exprType);
          newexprType.objectTypeDef = this.getTypeConfigsAsFrontend(exprType.objectTypeDef);
          return newexprType;
        }
      }
    }
    else {
      // scalar: apply secured
      const newexprType = this.cloneTypeConfigWithBaseProps(exprType);
      newexprType.type = exprType.isSecured === true && exprType.type === Type.number ? Type.string : exprType.type;
      return newexprType;
    }
  }

  override onValidate = (control: AbstractControl): ValidationErrors | null => {
    const exprType = this.ignoreSecure ? this.type : this.getTypeConfigAsFrontend(this.type);
    const validationErrors = this.context ? this.context.validateExpr(control.value, exprType) : [];
    return validationErrors.length !== 0 ? { typings: validationErrors } : null;
  };

  override onDidFocusEditor() {
    // Users can toggle tab trapping with Ctrl + M, but we want to not trap by default, see
    // https://github.com/microsoft/monaco-editor/issues/2373 and
    // https://github.com/microsoft/monaco-editor/issues/176
    this.editor.createContextKey('editorTabMovesFocus', true);
  }

  override onEditorInit(editor: monaco.editor.IStandaloneCodeEditor) {
    /**
     * Options for single line mode, see https://github.com/microsoft/monaco-editor/issues/2009
     */

    // disable 'Enter' key only when it will produce a line break
    editor.onKeyDown(e => {
      if (e.keyCode === monaco.KeyCode.Enter) {
        // We only prevent enter when the suggest model is not active
        if (editor.getContribution<IEditorContribution>('editor.contrib.suggestController').model.state === 0) {
          e.preventDefault();
        }
      }
    });
    // handle user paste
    editor.onDidPaste(e => {
      if (e.range.endLineNumber > 1) {
        let newContent = '';
        const model = editor.getModel();
        const lineCount = model.getLineCount();
        for (let i = 0; i < lineCount; i++) {
          newContent += model.getLineContent(i + 1);
        }
        model.setValue(newContent);
        editor.setPosition({ column: newContent.length + 1, lineNumber: 1 });
      }
    });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => { /* disable 'Find' widget */ });

    editor.addCommand(monaco.KeyCode.F1, () => { /* disable 'F1' command palette */ });

    // TODO: consider disablind some other commands like:
    // Peek Definition (Alt + F12), Rename Symbol (F2), etc.

    super.onEditorInit(editor);
  }
}
